import { useState, useRef, useEffect } from "react";

import { FormikProps } from "formik";
import { ClickAwayListener, InputAdornment, useMediaQuery } from "@mui/material";
import { MuiTelInput, MuiTelInputCountry } from "mui-tel-input";
import Image from "next/image";

import { TrackingLogic } from "@/src/logic";
import { StrapiCollectionType } from "@/src/type";
import { FormHelper, LoadFromCDN, UUIDHelper } from "@/src/util";

import { FormStyles } from "@/styles/util";

import styles from "./PhoneNumber.module.scss";

interface PhoneNumberProps {
  item: StrapiCollectionType.FormItem;
  isDark?: boolean;
  disableAnimatedLabel?: boolean;
  formik: FormikProps<any>;
}

function PhoneNumber({ item, isDark, disableAnimatedLabel, formik }: PhoneNumberProps) {
  const propertyName = item.definedProperty ?? item.jsonPropertyName;
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [defaultCountry, setDefaultCountry] = useState<MuiTelInputCountry>("US");
  const [isInitiating, setIsInitiating] = useState(true);
  const phoneInputRef = useRef<HTMLElement>();
  const heightToSmall = useMediaQuery("(max-width: 760px)");
  const [containerId] = useState<string>(UUIDHelper.createUUID(true));
  const id = btoa(`${item.id}-${item.name}`);

  const handleMenuOpen = () => {
    if (!menuOpen) {
      setMenuOpen(true);
    }
  };

  const handleMenuClose = () => {
    if (menuOpen) {
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    (async () => {
      const country = (await TrackingLogic.getLocationData())?.country ?? "US";
      setDefaultCountry(country as MuiTelInputCountry);
      setIsInitiating(false);
    })();
  }, []);

  useEffect(() => {
    if (isInitiating || !containerId) {
      return;
    }

    const button = document.querySelector(`div[id='${containerId}'] button[class*='MuiTelInput-IconButton']`);
    if (button) {
      button.addEventListener("click", handleMenuOpen);
    }

    return () => {
      if (button) {
        button.removeEventListener("click", handleMenuOpen);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId, isInitiating]);

  if (isInitiating) {
    return null;
  }

  return (
    <div id={containerId}>
      <ClickAwayListener onClickAway={handleMenuClose}>
        <MuiTelInput
          fullWidth
          className={styles.phoneNumberInput}
          id={id}
          name={propertyName}
          label={item.label}
          aria-label={item.label}
          placeholder={item.placeholder}
          value={formik.values[propertyName]}
          forceCallingCode={true}
          defaultCountry={defaultCountry ?? "US"}
          onChange={(value /*, countryInfo*/) => {
            formik.setFieldValue(propertyName, value);
          }}
          unknownFlagElement={
            item.startIcon ? (
              <InputAdornment position="start">
                <Image
                  src={LoadFromCDN.imageSizeAndQuality(item.startIcon?.data?.attributes?.url, 100, 24)}
                  width={24}
                  height={24}
                  alt={`${item.label} Icon`}
                />
              </InputAdornment>
            ) : (
              <Image
                src="/image/icon_phone_dark.svg"
                width={24}
                height={24}
                alt="World Icon"
              />
            )
          }
          sx={{
            ...FormStyles.themeStyle(isDark),
            ...FormStyles.handleLabel(formik.values[propertyName], disableAnimatedLabel),
            "& .MuiInputAdornment-positionStart": {
              "& .MuiTypography-body1": {
                paddingLeft: "4px",
                fontSize: "15px",
              },
            },
          }}
          InputProps={{
            ref: phoneInputRef,
          }}
          MenuProps={{
            open: menuOpen,
            variant: "menu",
            anchorEl: phoneInputRef.current,
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            componentsProps: {
              root: {
                style: {
                  top: "2px",
                },
              },
            },
            slotProps: {
              paper: {
                style: {
                  pointerEvents: "auto",
                  height: "auto",
                  maxHeight: heightToSmall ? "200px" : "200px",
                  boxShadow: "0px 5px 5px -3px rgba(0,0,0,0.3), 5px 0px 5px -3px rgba(0,0,0,0.3), -5px 0px 5px -3px rgba(0,0,0,0.3)",
                  maxWidth: phoneInputRef?.current?.getBoundingClientRect().width,
                },
              },
            },
          }}
          helperText={FormHelper.formikErrorMessage<any>(formik, propertyName)}
          error={FormHelper.formikCheckError<any>(formik, propertyName)}
        />
      </ClickAwayListener>
      <div className={styles.hidden}>
        <input
          type="text"
          name="fullPhoneNumber"
          id="fullPhoneNumber"
          value={formik.values[propertyName] ?? ""}
          readOnly={true}
          onClick={() => {}}
        />
      </div>
    </div>
  );
}

export default PhoneNumber;
